import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAppSelector } from '../hooks/reduxHooks';
import { selectPipelineReadiness } from '../store/workflowSelectors';
import { typography } from '~/theme';

interface StatusPanelProps {
  isTranscribing: boolean;
  isFinal: boolean;
  isFinalized?: boolean;
  isIncrementalCorrecting?: boolean;
  isRefiningInFlight?: boolean;
}

// ── Pipeline stage row ──────────────────────────────────────────────

type StageColor = 'success' | 'warning' | 'error' | 'default';

function PipelineRow({ label, ready, processing }: { label: string; ready: boolean; processing?: boolean }) {
  let color: StageColor = 'default';
  let text = 'Pending';
  if (ready) { color = 'success'; text = 'Ready'; }
  else if (processing) { color = 'warning'; text = 'Processing'; }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
        {label}
      </Typography>
      <Chip
        label={text}
        size="small"
        color={color}
        variant="outlined"
        sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.label.fontWeight, '& .MuiChip-label': { px: 0.75 } }}
      />
    </Box>
  );
}

const StatusPanel: React.FC<StatusPanelProps> = ({
  isTranscribing,
  isFinal,
  isFinalized = false,
  isIncrementalCorrecting = false,
  isRefiningInFlight = false,
}) => {
  const readiness = useAppSelector(selectPipelineReadiness);

  const getStatusChip = () => {
    if (isTranscribing) {
      return (
        <Chip
          icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} aria-hidden="true" />}
          label={isIncrementalCorrecting ? 'Live Review' : 'Dictating'}
          size="small"
          color={isIncrementalCorrecting ? 'success' : 'error'}
          variant="outlined"
          sx={{
            '& .MuiChip-icon': {
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.3 },
                '100%': { opacity: 1 },
              },
            },
          }}
        />
      );
    }
    if (isRefiningInFlight) {
      return (
        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} aria-hidden="true" />}
          label="Refining text..."
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    }
    if (isFinal) {
      return (
        <Chip
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} aria-hidden="true" />}
          label="Complete"
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    return (
      <Chip label="Ready" size="small" variant="outlined" />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight} textTransform="uppercase" letterSpacing={0.5}>
          Status
        </Typography>
        {getStatusChip()}
      </Box>

      {/* Pipeline Visibility Panel */}
      {isFinal && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight} textTransform="uppercase" letterSpacing={0.5} display="block" sx={{ mb: 0.5 }}>
            Pipeline
          </Typography>
          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1, py: 0.5 }}>
            <PipelineRow label="Transcript" ready={isFinalized || readiness.transcriptFinalized} />
            <PipelineRow label="Correction" ready={isFinalized || readiness.correctionCompleted} processing={!isFinalized && isRefiningInFlight} />
            <PipelineRow label="CPT Exam" ready={isFinalized || readiness.cptExamResolved} />
            <PipelineRow label="Template" ready={isFinalized || readiness.templateResolved} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                Pipeline Health
              </Typography>
              <Chip
                label={readiness.pipelineHealth === 'ready' ? 'Ready' : readiness.pipelineHealth === 'degraded' ? 'Degraded' : 'Blocked'}
                size="small"
                color={readiness.pipelineHealth === 'ready' ? 'success' : readiness.pipelineHealth === 'degraded' ? 'warning' : 'error'}
                variant="filled"
                sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight, '& .MuiChip-label': { px: 0.75 } }}
              />
            </Box>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default StatusPanel;
